import { useEffect, useId, useState } from "react";
import { Loader2, Lock, Store, UserPlus, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useCreateOutlet, useUpdateOutlet } from "@/hooks/useClients";
import { AddressPicker, emptyAddress, type AddressValue } from "./AddressPicker";
import type { CreateOutletPayload, OutletRow, OutletType, UpdateOutletPayload } from "@/api/clients";

/**
 * Этап 5 — outlet create/edit drawer (upgrades the FIX-1 minimal create). An
 * outlet is a master of the HOLDING; the operating legal entity and the point
 * contact are OPTIONAL. Address = NP picker with first-class manual fallback.
 *
 * sourceSystem doctrine (AC-8): a 1С/official outlet keeps name/type/address
 * read-only; only the CRM overlay (contact / status) is editable. CRM (manual)
 * outlets are fully editable.
 */

export const OUTLET_TYPE_LABELS: Record<string, string> = {
  "retail-store": "Зоомагазин",
  "vet-clinic": "Ветклініка",
  "grooming-salon": "Грумінг-салон",
  "warehouse": "Склад",
  "office": "Офіс",
  "e-commerce-pickup": "Пункт видачі",
  "delivery-only": "Тільки доставка",
};
const OUTLET_TYPES = Object.keys(OUTLET_TYPE_LABELS) as OutletType[];

interface OptionRow { id: string; name: string }

interface Props {
  open: boolean;
  holdingId: string;
  clientName: string;
  legalEntities?: OptionRow[];
  contacts?: OptionRow[];
  /** present → edit mode */
  editOutlet?: OutletRow | null;
  /** edit-mode «+ новий контакт точки» → host opens the Этап 2 drawer (outlet prefill). */
  onNewContact?: (outletId: string) => void;
  onClose: () => void;
}

export function CreateOutletDrawer({
  open, holdingId, clientName, legalEntities = [], contacts = [], editOutlet, onNewContact, onClose,
}: Props) {
  const headingId = useId();
  const panelRef = useDrawerA11y(open, onClose);
  const createMut = useCreateOutlet(holdingId);
  const updateMut = useUpdateOutlet(holdingId);
  const isEdit = !!editOutlet;
  const official = !!editOutlet?.official;

  const [name, setName] = useState("");
  const [outletType, setOutletType] = useState<OutletType>("retail-store");
  const [address, setAddress] = useState<AddressValue>(emptyAddress);
  const [companyId, setCompanyId] = useState("");
  const [contactId, setContactId] = useState("");
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (editOutlet) {
      setName(editOutlet.name);
      setOutletType((editOutlet.type as OutletType) || "retail-store");
      setCompanyId(editOutlet.companyId ?? "");
      setContactId("");
      setActive(editOutlet.active);
      // Edit re-picks the address; seed manual fallback with the known city/street.
      setAddress({
        addressSource: "manual", npAreaRef: null, npSettlementRef: null, npStreetRef: null,
        city: editOutlet.city ?? undefined, streetText: editOutlet.address ?? undefined,
      });
    } else {
      setName(""); setOutletType("retail-store"); setAddress(emptyAddress);
      setCompanyId(""); setContactId(""); setActive(true);
    }
  }, [open, editOutlet]);

  if (!open) return null;

  const trimmedName = name.trim();
  const pending = createMut.isPending || updateMut.isPending;
  const canSubmit = trimmedName !== "" && !pending;

  const addressPayload = () => ({
    addressSource: address.addressSource,
    npAreaRef: address.npAreaRef ?? null,
    npSettlementRef: address.npSettlementRef ?? null,
    npStreetRef: address.npStreetRef ?? null,
    city: address.city,
    streetText: address.streetText,
    buildingText: address.buildingText,
  });

  const submit = () => {
    if (!canSubmit) return;
    if (isEdit) {
      const payload: UpdateOutletPayload = { active, primaryContactId: contactId || undefined };
      if (!official) {
        payload.name = trimmedName;
        payload.outletType = outletType;
        payload.companyId = companyId || null;
        Object.assign(payload, addressPayload());
      }
      updateMut.mutate({ outletId: editOutlet!.id, payload }, { onSuccess: () => onClose() });
    } else {
      const payload: CreateOutletPayload = { name: trimmedName, outletType, active, ...addressPayload() };
      if (companyId) payload.companyId = companyId;
      if (contactId) payload.primaryContactId = contactId;
      createMut.mutate(payload, { onSuccess: () => onClose() });
    }
  };

  const labelCls = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";
  const fieldCls = "w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const err = (createMut.error || updateMut.error) as Error | null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col overflow-y-auto bg-background shadow-2xl outline-none sm:w-[470px]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <div className="min-w-0">
            <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
              <Store className="h-4 w-4 text-muted-foreground" /> {isEdit ? "Редагувати точку" : "Додати точку"}
              {official && <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"><Lock className="h-2.5 w-2.5" />з 1С</span>}
            </h3>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{clientName}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 px-5 py-5">
          {official && (
            <p className="rounded-md border border-border bg-muted px-3 py-2 text-[11.5px] text-muted-foreground">
              Офіційні поля (назва/тип/адреса) — з 1С, read-only. Редагується лише CRM-оверлей: контакт точки та статус.
            </p>
          )}

          <div className="space-y-1.5">
            <label className={labelCls}>Назва точки <span className="text-destructive">*</span></label>
            <input type="text" value={name} autoFocus={!isEdit} disabled={official} onChange={(e) => setName(e.target.value)}
              placeholder="напр. Зоомагазин на Хрещатику" className={cn(fieldCls, official && "opacity-60")} />
          </div>

          <div className="space-y-1.5">
            <label className={labelCls}>Тип точки</label>
            <select value={outletType} disabled={official} onChange={(e) => setOutletType(e.target.value as OutletType)} className={cn(fieldCls, official && "opacity-60")}>
              {OUTLET_TYPES.map((t) => <option key={t} value={t}>{OUTLET_TYPE_LABELS[t]}</option>)}
            </select>
          </div>

          {/* address picker — hidden read-only note for official outlets */}
          <div className="space-y-1.5">
            <label className={labelCls}>Адреса <span className="font-normal normal-case text-muted-foreground">— довідник Нова Пошта + ручний ввід</span></label>
            {official ? (
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                {editOutlet?.city ? `${editOutlet.city}, ` : ""}{editOutlet?.address ?? "—"} <span className="text-[11px]">(read-only)</span>
              </div>
            ) : (
              <AddressPicker value={address} onChange={setAddress} />
            )}
          </div>

          {/* legal entity (operator) */}
          {legalEntities.length > 0 && (
            <div className="space-y-1.5">
              <label className={labelCls}>Юрособа-оператор <span className="font-normal normal-case">(необовʼязково)</span></label>
              <select value={companyId} disabled={official} onChange={(e) => setCompanyId(e.target.value)} className={cn(fieldCls, official && "opacity-60")}>
                <option value="">— не вказано —</option>
                {legalEntities.map((le) => <option key={le.id} value={le.id}>{le.name}</option>)}
              </select>
              {!companyId && (
                <p className="flex items-center gap-1.5 rounded-md bg-warning px-2.5 py-1.5 text-[11px] text-warning-foreground">
                  ⚠ Не вказано юрособу для документів / оплат
                </p>
              )}
            </div>
          )}

          {/* point contact (CRM overlay — editable even for 1С) */}
          <div className="space-y-1.5">
            <label className={labelCls}>Контакт точки <span className="font-normal normal-case">(необовʼязково)</span></label>
            <select value={contactId} onChange={(e) => setContactId(e.target.value)} className={fieldCls}>
              <option value="">{isEdit && editOutlet?.contactName ? `поточний: ${editOutlet.contactName} — без змін` : "— не вказано —"}</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {isEdit && onNewContact && (
              <button type="button" onClick={() => onNewContact(editOutlet!.id)} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-primary hover:underline">
                <UserPlus className="h-3.5 w-3.5" /> Новий контакт точки (форма Контактів з prefill точки)
              </button>
            )}
          </div>

          <div className="space-y-1.5">
            <label className={labelCls}>Статус</label>
            <select value={active ? "active" : "inactive"} onChange={(e) => setActive(e.target.value === "active")} className={fieldCls}>
              <option value="active">Активна</option>
              <option value="inactive">Неактивна</option>
            </select>
          </div>

          {err && (
            <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {err.message ?? "Не вдалося зберегти точку."}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-background px-5 py-4">
          <button type="button" onClick={onClose} className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">Скасувати</button>
          <button type="button" disabled={!canSubmit} onClick={submit}
            className={cn("inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90", !canSubmit && "cursor-not-allowed opacity-50")}>
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Зберегти" : "Зберегти точку"}
          </button>
        </div>
      </div>
    </div>
  );
}
